import { create } from 'zustand';
import axios from 'axios';
import { toast } from 'sonner';
import { salesOrderService } from '../services/salesOrderService';
import { firmOfferService } from '../services/firmOfferService';
import { customerAddressService } from '../services/customerAddressService';
import { extractPricingDrift, type PricingDriftDetail } from '@features/quotations/types/pricingDrift.types';
import { applyFoPriceLevels } from '../lib/foPriceLevels';
import { separateBillingFromCustomer } from '../utils/freight';
import { lineWeightKg, PIECE_UNIT } from '../utils/lineWeight';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import {
  extractLineItemsFromSource,
  joinFileNamesCapped,
  type IntakeSource,
} from '../lib/intakeExtraction';
import {
  type AddressDto,
  type AppendAddressRequest,
  type CustomerCompanyProfile,
  type CustomerHierarchyCompany,
  type FirmOfferDetailDto,
  type FirmOfferSummaryDto,
  type SalesOrderDto,
  type SalesOrderListItemDto,
  type SaveSalesOrderDraftRequest,
  SalesOrderStatus,
  type SubmitSalesOrderRequest,
} from '../types/salesOrder.types';

// FO-SOI-3: Zustand store for the Sales Order Intake compose page + list page.
// Slimmer than the Quotation store: single active draft (no multi-tab strip in
// v1), no AutoDraft inbox, no markup recompute. Realtime list updates ride the
// SSE bus separately (see useSalesOrderEventStream).

interface SalesOrderState {
  // List page state
  list: SalesOrderListItemDto[];
  totalCount: number;
  isLoadingList: boolean;
  listError: string | null;

  // Active draft (compose page binds to this)
  activeDraft: SalesOrderDto | null;
  isLoadingDraft: boolean;
  loadDraftError: string | null;
  isSaving: boolean;
  saveError: string | null;
  isSubmitting: boolean;
  submitError: string | null;
  /** Structured pricing-drift payload that drives the PricingDriftDialog. 86d3ah33r: no
   *  longer set from a synchronous 409 (the post is async) — the detail panel populates it
   *  from a PostingFailed draft's postingDriftJson when the coordinator hits Refresh-and-resubmit. */
  pricingDrift: PricingDriftDetail | null;
  /** 86d3ah33r — ids this client moved to Posting this session. ⚠️ Currently written but never
   *  read (14ykpqtr2kp review, 2026-09-29): the result toasts moved to the notification centre (N8)
   *  and `ackPostingResult` has no callers. The panel's read-only gate and posting card key off the
   *  outbox status, and it re-fetches on the SSE status change — not off this set. */
  postingDraftIds: Set<number>;
  lastSavedAt: string | null;
  /** Set true on any field edit, cleared by saveDraft / loadDraft / startNewDraft.
   *  Drives the workspace's debounced auto-save effect (timer lives in the
   *  detail panel, not the store, so unmount cancels naturally). */
  dirty: boolean;

  // FO picker state
  foSearchResults: FirmOfferSummaryDto[];
  isSearchingFo: boolean;
  selectedFirmOffer: FirmOfferDetailDto | null;
  /** The list-row summary of the FO currently being loaded — drives the instant
   *  header + skeleton before `getById` resolves. Cleared once detail arrives. */
  pendingFoSummary: FirmOfferSummaryDto | null;
  isLoadingFirmOffer: boolean;

  /** Id of the draft whose line-item extraction is in flight, or null. Scoped by
   *  id so the "Extracting…" badge follows the right draft if the coordinator
   *  switches drafts mid-extraction (86d3a2agk). */
  extractingDraftId: number | null;

  // Address book for the selected Deliver/Bill-to company
  customerAddresses: AddressDto[];
  isLoadingAddresses: boolean;

  // Deliver/Bill-to company hierarchy + selected-company profile (FO-SOI-3)
  customerHierarchy: CustomerHierarchyCompany[];
  isLoadingHierarchy: boolean;
  companyProfile: CustomerCompanyProfile | null;
  isLoadingProfile: boolean;

  // Reopen-eligibility banner
  eligibilityBanner: string | null;

  // ─── List actions ───────────────────────────────────────────────────────
  loadList: (params?: { statuses?: SalesOrderStatus[]; search?: string; includeArchived?: boolean; page?: number; pageSize?: number }) => Promise<void>;
  spliceRow: (row: SalesOrderListItemDto) => void;
  removeRow: (id: number) => void;

  // ─── Compose actions ────────────────────────────────────────────────────
  startNewDraft: () => void;
  loadDraft: (id: number) => Promise<void>;
  updateDraftFields: (fields: Partial<SalesOrderDto>) => void;
  /** Run AI extraction for `source` against the active draft and splice the
   *  matched line items in. Owns the fetch (was in SalesOrderIntakePanel) so the
   *  modal can fire-and-close while extraction runs in the background. */
  runExtraction: (source: IntakeSource) => Promise<void>;
  saveDraft: () => Promise<SalesOrderDto | null>;
  /** 86d3ah33r: CLAIMS the draft for async posting (202). Returns true when the claim was
   *  accepted (the worker takes over; result arrives via SSE), false on a rejected claim.
   *  Also serves Retry / Refresh-and-resubmit from a PostingFailed draft. */
  submitDraft: (request: SubmitSalesOrderRequest) => Promise<boolean>;
  clearPricingDrift: () => void;
  /** Open the PricingDriftDialog for a PostingFailed-with-drift draft (Refresh-and-resubmit). */
  showPricingDrift: (detail: PricingDriftDetail) => void;
  /** Drop an id from the toast-scoping set once its result toast has fired. */
  ackPostingResult: (id: number) => void;
  /** 86d3ah33r — re-fetch the open draft in place (status + posting fields) when its async
   *  posting result lands via SSE (Posting -> Submitted/PostingFailed). Lightweight: leaves
   *  the loaded FO/selection alone; only swaps activeDraft if it's still the same id. */
  refreshActiveDraft: (id: number) => Promise<void>;
  archiveDraft: (id: number) => Promise<void>;
  restoreDraft: (id: number) => Promise<void>;
  deleteDraft: (id: number) => Promise<void>;

  // ─── FO picker actions ──────────────────────────────────────────────────
  searchFirmOffers: (query: string | null, customerId: string | null, take?: number, mine?: boolean) => Promise<void>;
  selectFirmOffer: (summary: FirmOfferSummaryDto | null) => Promise<FirmOfferDetailDto | null>;
  /** (Re)load the active draft's linked FO + its address book. Retry target for
   *  the eligibility/connectivity banner; distinguishes 404-ineligible from a
   *  transient NetSuite/network failure. */
  loadLinkedFirmOffer: () => Promise<void>;

  // ─── Address book + eligibility ─────────────────────────────────────────
  // `loadSeq` (optional) is the load-generation token: when passed, the fetch drops its
  // result if a newer draft load OR company pick has superseded it (86d3achpu; picks added
  // 86d3c5wwk). Still omitted by selectFirmOffer (no dependent-default cascade to supersede).
  loadCustomerAddresses: (customerId: string, signal?: AbortSignal, applyDefaults?: boolean, loadSeq?: number) => Promise<void>;
  appendCustomerAddress: (customerId: string, request: AppendAddressRequest) => Promise<string>;
  setEligibilityBanner: (msg: string | null) => void;

  // ─── Deliver/Bill-to company cascade (FO-SOI-3) ─────────────────────────
  loadHierarchy: (customerId: string, loadSeq?: number) => Promise<void>;
  loadCompanyProfile: (companyId: string, applyDefaults?: boolean, loadSeq?: number) => Promise<void>;
  // Explicit company change → reset shipping/billing to that company's defaults.
  // Resolves `true` if this pick is still the latest when its cascade settles, `false` if a
  // newer pick (or draft switch) superseded it — the caller skips its form re-seed (86d3c5wwk).
  selectDoCustCompany: (companyId: string, companyName: string) => Promise<boolean>;
}

// Tracks the in-flight FO load so a rapid re-select can abort the previous one.
// Lives outside the store state: it must not trigger renders and is a singleton
// alongside the store itself.
let foAbortController: AbortController | null = null;

// Monotonic token guarding draft loads against the last-write-wins race when the
// user switches drafts faster than the fetches resolve (86d3achpu). Only loadDraft
// increments it; the chained FO + company sub-loads capture the current value and
// bail before writing if a newer draft load has superseded them — otherwise a stale
// response would clobber the active draft (and re-fire the load effect into a
// flicker cascade). Mirrors the foAbortController precedent above; lives outside
// store state so it never triggers renders.
let draftLoadSeq = 0;
const isStaleLoad = (seq: number | undefined): boolean =>
  seq !== undefined && seq !== draftLoadSeq;

// A 404 from the FO detail endpoint means the FO is genuinely ineligible (not
// Awarded / balance ≤ 0 / not found). ANY other failure — 502 from a NetSuite
// gateway/DNS error, a network drop, a timeout — is transient and retryable, and
// must NOT be reported to the user as "no longer eligible".
function isIneligibleError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 404;
}

export const useSalesOrderStore = create<SalesOrderState>()((set, get) => ({
  list: [],
  totalCount: 0,
  isLoadingList: false,
  listError: null,

  activeDraft: null,
  isLoadingDraft: false,
  loadDraftError: null,
  isSaving: false,
  saveError: null,
  isSubmitting: false,
  submitError: null,
  pricingDrift: null,
  postingDraftIds: new Set<number>(),
  lastSavedAt: null,
  dirty: false,

  foSearchResults: [],
  isSearchingFo: false,
  selectedFirmOffer: null,
  extractingDraftId: null,
  pendingFoSummary: null,
  isLoadingFirmOffer: false,

  customerAddresses: [],
  isLoadingAddresses: false,

  customerHierarchy: [],
  isLoadingHierarchy: false,
  companyProfile: null,
  isLoadingProfile: false,

  eligibilityBanner: null,

  loadList: async (params = {}) => {
    set({ isLoadingList: true, listError: null });
    try {
      const result = await salesOrderService.getPaged({
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
        sortBy: 'updatedAt',
        sortDesc: true,
        statuses: params.statuses,
        search: params.search,
        includeArchived: params.includeArchived ?? false,
      });
      set({ list: result.items, totalCount: result.totalCount, isLoadingList: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load sales orders';
      set({ listError: message, isLoadingList: false });
      toast.error(message);
    }
  },

  spliceRow: (row) => {
    set((state) => {
      const idx = state.list.findIndex((r) => r.id === row.id);
      if (idx === -1) return { list: [row, ...state.list], totalCount: state.totalCount + 1 };
      const next = [...state.list];
      next[idx] = row;
      return { list: next };
    });
  },

  removeRow: (id) => {
    set((state) => {
      const next = state.list.filter((r) => r.id !== id);
      const removed = next.length < state.list.length;
      return { list: next, totalCount: removed ? state.totalCount - 1 : state.totalCount };
    });
  },

  startNewDraft: () => {
    set({
      activeDraft: createBlankDraft(),
      lastSavedAt: null,
      saveError: null,
      submitError: null,
      selectedFirmOffer: null,
      pendingFoSummary: null,
      customerAddresses: [],
      customerHierarchy: [],
      companyProfile: null,
      eligibilityBanner: null,
      dirty: false,
    });
  },

  loadDraft: async (id) => {
    // Claim the latest-load slot. Any older in-flight loadDraft (and its chained
    // FO/company sub-loads) becomes stale the moment this increments (86d3achpu).
    const seq = ++draftLoadSeq;
    set({ isLoadingDraft: true, loadDraftError: null });
    try {
      const draft = await salesOrderService.getById(id);
      // A newer draft selection superseded this load while getById was in flight —
      // drop the stale result rather than clobbering activeDraft (which would also
      // re-fire the load effect into a flicker cascade).
      if (isStaleLoad(seq)) return;
      // Drop the previous draft's FO immediately so a draft switch shows the
      // loading shell + "Syncing…" indicator (not stale FO data) while the new FO
      // re-fetches. Flag the load now when the draft has an FO so foLoading is true
      // from the first render — no flash of the no-FO placeholder before
      // loadLinkedFirmOffer runs (86d3abjqx). On a fresh F5 reload these are already
      // at their initial values, so this is a no-op there.
      // 86d3c2wd1: a SUBMITTED SO renders from frozen DOP data — no live FO/address-book
      // re-fetch. Don't flag foLoading for it (nothing will clear it) and skip the fetch below.
      const reloadingFo =
        Boolean(draft.linkedFirmOfferInternalId) && draft.status !== SalesOrderStatus.Submitted;
      set({
        activeDraft: draft,
        isLoadingDraft: false,
        lastSavedAt: draft.updatedAt,
        dirty: false,
        selectedFirmOffer: null,
        isLoadingFirmOffer: reloadingFo,
      });

      // Reopen-eligibility check: re-fetch the linked FO + its address book.
      // Distinguishes a genuine "no longer eligible" (404) from a transient
      // NetSuite/connectivity failure — see loadLinkedFirmOffer. Called with no
      // await-gap after the seq check, so it captures this same (still-current) seq.
      if (reloadingFo) {
        await get().loadLinkedFirmOffer();
      }
    } catch (err) {
      // Stale failure — a newer load owns the panel now; don't surface this error.
      if (isStaleLoad(seq)) return;
      const message = err instanceof Error ? err.message : 'Failed to load sales order';
      set({ loadDraftError: message, isLoadingDraft: false });
      toast.error(message);
    }
  },

  updateDraftFields: (fields) => {
    set((state) =>
      state.activeDraft
        ? { activeDraft: { ...state.activeDraft, ...fields }, dirty: true }
        : state,
    );
  },

  runExtraction: async (source) => {
    const draft = get().activeDraft;
    if (!draft) return;
    const draftId = draft.id;
    set({ extractingDraftId: draftId });
    try {
      const { items, droppedKgLineCount } = await extractLineItemsFromSource(source);
      if (items.length === 0) {
        toast.error('No line items were extracted from the provided sources.');
        return;
      }
      // Guard: the coordinator may have switched drafts while the AI ran — never
      // splice the result into a different draft than the one we extracted for.
      const current = get().activeDraft;
      if (!current || current.id !== draftId) return;
      const fileNames = source.files.map((f) => f.name);
      get().updateDraftFields({
        lineItems: applyFoPriceLevels(items, get().selectedFirmOffer?.perGradePriceLevel),
        extractionText: source.text.trim() || null,
        extractionFileName: fileNames.length > 0 ? joinFileNamesCapped(fileNames) : null,
        extractionSource: 'intake',
      });
      toast.success(`Extracted ${items.length} line items`);
      if (droppedKgLineCount > 0) {
        // 14ykpqtv3qw — the document listed those bars in pieces too; keeping both would double them.
        toast.info(
          `Skipped ${droppedKgLineCount} kg line${droppedKgLineCount === 1 ? '' : 's'} — the document lists the same bars in pieces.`,
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Extraction failed');
    } finally {
      // Only clear the slot if a newer extraction hasn't taken it over.
      if (get().extractingDraftId === draftId) set({ extractingDraftId: null });
    }
  },

  saveDraft: async () => {
    const draft = get().activeDraft;
    if (!draft) return null;
    // Identity captured for the draft-switch guard below (86d3ckf8h).
    const savedId = draft.id;
    set({ isSaving: true, saveError: null });
    try {
      const request = buildSaveRequest(draft);
      const saved = await salesOrderService.saveDraft(request);
      // 86d3ckf8h: MERGE only the server-authoritative fields into the EXISTING draft —
      // keep `lineItems` + every other field locally-authoritative. The old wholesale
      // `set({ activeDraft: saved })` re-created the lineItems array on every autosave,
      // re-rendering the line-items table mid-interaction (closed open popovers like the
      // Length select) AND silently clobbered edits made between save-fire and
      // response-land. Mirrors quotationDraftStore.saveDraft.
      //
      // Guard a draft switch during the round-trip: only apply if the saved draft is still
      // active. id === 0 (a brand-new draft's first save) is safe — the workspace serializes
      // draft switches (one new draft in flight; loadDraft / draftLoadSeq guard).
      if (get().activeDraft?.id !== savedId) {
        set({ isSaving: false });
        return null;
      }
      const merged: SalesOrderDto = {
        ...get().activeDraft!,
        id: saved.id,
        salesOrderNumber: saved.salesOrderNumber,
        status: saved.status,
        createdAt: saved.createdAt,
        updatedAt: saved.updatedAt,
      };
      set({
        activeDraft: merged,
        isSaving: false,
        lastSavedAt: saved.updatedAt ?? saved.createdAt,
        dirty: false,
      });
      return merged;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save draft';
      set({ saveError: message, isSaving: false });
      toast.error(message);
      return null;
    }
  },

  submitDraft: async (request) => {
    set({ isSubmitting: true, submitError: null, pricingDrift: null });
    try {
      // 86d3ah33r: claims the draft (202) — the slow NetSuite post runs in the worker and the
      // result (Submitted / PostingFailed) lands via SSE. No created DTO comes back here.
      await salesOrderService.submit(request.salesOrderId, request);
      // Record the claim in postingDraftIds (⚠️ written but never read — see its declaration). The
      // double-submit window is closed by the PANEL, not here: on a true return it sets an optimistic
      // Posting job (86d3peur0 #12), which engages the read-only gate at once. The terminal result lands
      // via the SSE status-change refresh effect (14ykpqtr2kp); no getById needed here.
      set((s) => {
        const postingDraftIds = new Set(s.postingDraftIds);
        postingDraftIds.add(request.salesOrderId);
        return { postingDraftIds, isSubmitting: false };
      });
      toast.info('Posting started — you can keep working.');
      return true;
    } catch (err) {
      // 86d3mnuxe FIX 5 — drift is a synchronous 409 at the claim (decide-at-submit): surface the
      // before/after dialog while the coordinator is present. SO is refresh-only (owner ruling —
      // the FO is a contract, so the keep-old choice isn't offered); the dialog's confirm re-pulls
      // the FO + resubmits (handleRefreshAndResubmit).
      const drift = extractPricingDrift(err);
      if (drift) {
        set({ pricingDrift: drift, isSubmitting: false });
        return false;
      }
      // 14ykpqtv3qx — the server's own words (e.g. how far over the Firm Offer balance), not Axios's
      // "Request failed with status code 400", which is all the coordinator saw before.
      const message = getApiErrorMessage(err, 'Submit failed');
      set({ submitError: message, isSubmitting: false });
      toast.error(message);
      return false;
    }
  },

  clearPricingDrift: () => set({ pricingDrift: null }),

  showPricingDrift: (detail) => set({ pricingDrift: detail }),

  ackPostingResult: (id) =>
    set((s) => {
      if (!s.postingDraftIds.has(id)) return {};
      const postingDraftIds = new Set(s.postingDraftIds);
      postingDraftIds.delete(id);
      return { postingDraftIds };
    }),

  refreshActiveDraft: async (id) => {
    if (get().activeDraft?.id !== id) return;
    try {
      const refreshed = await salesOrderService.getById(id);
      set((s) => (s.activeDraft?.id === id ? { activeDraft: refreshed } : {}));
    } catch {
      // Non-fatal — the master-list row already reflects the new status via SSE.
    }
  },

  archiveDraft: async (id) => {
    try {
      await salesOrderService.archive(id);
      toast.success('Sales order archived');
      get().removeRow(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Archive failed');
    }
  },

  // 86d3d2hvz: un-archive. Mirrors archiveDraft (removeRow drops it from the Archived tab),
  // and additionally flips the open draft's isArchived so the workspace returns to editable
  // in place (no reload) — autosave/Save/Create come back via the isEditable gate.
  restoreDraft: async (id) => {
    try {
      await salesOrderService.restore(id);
      toast.success('Sales order restored');
      get().removeRow(id);
      const active = get().activeDraft;
      if (active && active.id === id) {
        set({ activeDraft: { ...active, isArchived: false } });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Restore failed');
    }
  },

  deleteDraft: async (id) => {
    try {
      await salesOrderService.delete(id);
      toast.success('Sales order deleted');
      get().removeRow(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  },

  searchFirmOffers: async (query, customerId, take, mine = false) => {
    set({ isSearchingFo: true });
    try {
      const results = await firmOfferService.search(query, customerId, take, mine);
      set({ foSearchResults: results, isSearchingFo: false });
    } catch (err) {
      set({ isSearchingFo: false });
      toast.error(err instanceof Error ? err.message : 'Failed to search firm offers');
    }
  },

  selectFirmOffer: async (summary) => {
    // Abort any in-flight FO load so a rapid re-select can't render stale data
    // or fire a false eligibility error.
    foAbortController?.abort();

    if (!summary) {
      foAbortController = null;
      set({
        selectedFirmOffer: null,
        pendingFoSummary: null,
        customerAddresses: [],
        isLoadingFirmOffer: false,
        eligibilityBanner: null,
      });
      return null;
    }

    const controller = new AbortController();
    foAbortController = controller;
    const { signal } = controller;

    // Instant feedback: render the summary header now; clear stale detail/banner.
    set({
      pendingFoSummary: summary,
      selectedFirmOffer: null,
      customerAddresses: [],
      isLoadingFirmOffer: true,
      eligibilityBanner: null,
    });

    // Parallel: FO detail + the customer's address book. Addresses are keyed by
    // the summary's customerId so they don't wait on getById; they handle their
    // own errors inside loadCustomerAddresses.
    const detailPromise = firmOfferService.getById(summary.internalId, signal);
    // Deliver/Bill-to defaults to the FO customer; its address book loads with
    // applyDefaults so a fresh pick gets the customer's default shipping + billing.
    void get().loadCustomerAddresses(summary.customerId, signal, true);
    void get().loadHierarchy(summary.customerId);
    void get().loadCompanyProfile(summary.customerId, true);

    try {
      const fo = await detailPromise;
      if (signal.aborted) return null; // superseded by a newer select — don't clobber
      set({ selectedFirmOffer: fo, isLoadingFirmOffer: false });

      // Hydrate the draft's inherited fields from the FO snapshot (detail-only)
      const draft = get().activeDraft;
      if (draft) {
        get().updateDraftFields({
          linkedFirmOfferInternalId: fo.internalId,
          linkedFirmOfferTranId: fo.tranId,
          customerName: fo.customer.refName ?? '',
          customerId: fo.customer.id,
          // Deliver/Bill-to defaults to the SO entity (the FO customer); the
          // coordinator can switch to a sibling company in the hierarchy.
          doCustCompanyId: fo.customer.id,
          doCustCompanyName: fo.customer.refName ?? '',
          headMainOffice: fo.headMainOffice?.refName ?? '',
          headMainOfficeCustomerId: fo.headMainOffice?.id ?? null,
          projectName: fo.projectName ?? '',
          customerPoNumber: fo.customerPoNumber ?? null,
          contractExpiryDate: fo.contractExpiryDate ?? null,
          termsId: fo.terms?.id ?? null,
          termsName: fo.terms?.refName ?? null,
          salesRepId: fo.salesRep?.id ?? null,
          salesRepName: fo.salesRep?.refName ?? null,
          salesCoordId: fo.salesCoordinator?.id ?? null,
          salesCoordName: fo.salesCoordinator?.refName ?? null,
          soRemarks: fo.memo ?? null,
          waivedFreight: fo.waivedFreight?.id ?? '2',
          // Pricing carry-over snapshot
          grade230Markup: fo.pricing.perGrade.grade230.markupPct,
          grade275Markup: fo.pricing.perGrade.grade280.markupPct,
          grade415Markup: fo.pricing.perGrade.grade420.markupPct,
          grade520Markup: fo.pricing.perGrade.grade520.markupPct,
          grade550Markup: fo.pricing.perGrade.grade550.markupPct,
          overallPricePerKg: fo.pricing.overallPricePerKg,
          pricePerKg230G33: fo.pricing.perGrade.grade230.pricePerKg,
          pricePerKg280G40Stv: fo.pricing.perGrade.grade280.pricePerKg,
          pricePerKg420G60Stv: fo.pricing.perGrade.grade420.pricePerKg,
          pricePerKg520G75Stv: fo.pricing.perGrade.grade520.pricePerKg,
          pricePerKg550G80Stv: fo.pricing.perGrade.grade550.pricePerKg,
          totalAuthorizedKg: fo.totalAuthorizedKg,
          // 86d3c2wd1: freeze the FO customer's rebar VAT rate so the read-only Pricing tab
          // computes gross from frozen data (no live FO on a submitted SO).
          rebarTaxRate: fo.pricing.rebarTaxRate,
          // 86d3cwpzv: freeze the FO freight-per-ton rate so the read-only Freight & Order
          // Total block can show freight without a live FO.
          freightChargePerTon: fo.freightChargePerTon,
          perGradeBalanceKgJson: JSON.stringify(fo.perGradeBalanceKg),
          // Backfill any blank matched lines' price level from the FO (in-memory map).
          lineItems: applyFoPriceLevels(draft.lineItems, fo.perGradePriceLevel),
        });
      }
      return fo;
    } catch (err) {
      // A cancelled request (superseded by a newer select) is not a failure —
      // stay silent so it never flashes a false "no longer eligible" banner.
      if (axios.isCancel(err) || signal.aborted) return null;
      // Only a 404 means the FO is genuinely ineligible; anything else (502,
      // network/DNS, timeout) is a transient NetSuite-reachability failure and
      // must NOT be reported as ineligibility.
      set({
        isLoadingFirmOffer: false,
        eligibilityBanner: isIneligibleError(err)
          ? 'This Firm Offer is no longer eligible — pick another.'
          : "Couldn't reach NetSuite to load this Firm Offer. Check your connection and retry.",
      });
      toast.error(err instanceof Error ? err.message : 'Failed to load Firm Offer');
      return null;
    }
  },

  loadLinkedFirmOffer: async () => {
    const draft = get().activeDraft;
    if (!draft?.linkedFirmOfferInternalId) return;
    // Capture the CURRENT draft-load token (do not increment — only loadDraft does).
    // When called from loadDraft this matches that load's seq; when called standalone
    // (drift refresh-and-resubmit / eligibility retry) it matches whatever load owns
    // the panel. Either way a later draft switch bumps draftLoadSeq and supersedes us.
    const seq = draftLoadSeq;
    set({ isLoadingFirmOffer: true, eligibilityBanner: null });
    try {
      const fo = await firmOfferService.getById(draft.linkedFirmOfferInternalId);
      // Superseded by a newer draft load while the FO was in flight — drop it so it
      // can't clobber the current draft's FO/pricing area.
      if (isStaleLoad(seq)) return;
      set({ selectedFirmOffer: fo, eligibilityBanner: null, isLoadingFirmOffer: false });

      // Backfill blank matched lines' price level from the FO on reopen (fixes drafts
      // saved before a price level was defaulted). Only persist when something changed
      // so reopening a fully-stamped draft doesn't spuriously dirty it.
      const stamped = applyFoPriceLevels(draft.lineItems, fo.perGradePriceLevel);
      if (stamped.some((li, i) => li.priceLevel !== draft.lineItems[i]?.priceLevel)) {
        get().updateDraftFields({ lineItems: stamped });
      }

      if (draft.customerId) {
        // Address book + profile follow the persisted Deliver/Bill-to company
        // (falls back to the FO customer). No applyDefaults on reopen — the saved
        // shipping/billing picks must survive verbatim.
        const company = draft.doCustCompanyId ?? draft.customerId;
        // Forward the load token so these fire-and-forget sub-fetches also drop their
        // result if a newer draft load supersedes them mid-flight (86d3achpu).
        void get().loadHierarchy(draft.customerId, seq);
        void get().loadCompanyProfile(company, false, seq);
        await get().loadCustomerAddresses(company, undefined, false, seq);
      }
    } catch (err) {
      // Superseded mid-flight — a newer load owns the FO area now; stay silent.
      if (isStaleLoad(seq)) return;
      // 404 = genuinely no longer eligible; everything else (502 / network / DNS /
      // timeout) is a transient NetSuite-reachability failure — show a retryable
      // message instead of mislabeling the FO as ineligible.
      set({
        selectedFirmOffer: null,
        isLoadingFirmOffer: false,
        eligibilityBanner: isIneligibleError(err)
          ? 'This Firm Offer is no longer eligible — pick another to continue.'
          : "Couldn't reach NetSuite to verify this Firm Offer. Check your connection and retry.",
      });
    }
  },

  loadCustomerAddresses: async (customerId, signal, applyDefaults = false, loadSeq) => {
    set({ isLoadingAddresses: true });
    try {
      const addresses = await customerAddressService.list(customerId, signal);
      if (signal?.aborted) return;
      // Superseded by a newer draft load — drop the stale address book.
      if (isStaleLoad(loadSeq)) return;

      // 86d3c967d: NetSuite's required SHIPPING ADDRESS text is populated from the selected
      // address's text. A customer's auto-synced "main" address (the null-label entry, often
      // flagged default-shipping) has EMPTY address text — selecting it leaves SHIPPING
      // ADDRESS blank, so the post fails with "Please enter value(s) for: Shipping Address".
      // The real discriminator is empty `addrText` (NOT the null label, which only correlates).
      // Drop text-less entries here, once, so BOTH pickers and the default cascade below only
      // ever offer/pick a postable address. When the only default-shipping is text-less, the
      // cascade leaves shipping empty so the required prompt forces a usable pick.
      const selectable = addresses.filter((a) => !!a.addrText && a.addrText.trim().length > 0);
      set({ customerAddresses: selectable, isLoadingAddresses: false });

      // Cascade defaults (FO-SOI-3): point shipping at the company's default
      // shipping address and billing at its default billing address, clearing any
      // custom-address fields. Only on explicit pick / company change — never on
      // draft reopen (which passes applyDefaults=false to preserve saved picks).
      if (applyDefaults) {
        const defShip = selectable.find((a) => a.defaultShipping) ?? null;
        const defBill = selectable.find((a) => a.defaultBilling) ?? null;
        get().updateDraftFields({
          shippingAddressId: defShip?.internalId ?? null,
          shippingAddresseeName: null,
          shippingAddrLine1: null,
          shippingAddrLine2: null,
          shippingAddrLine3: null,
          shippingAddressOverrideJson: null,
          billingAddressId: defBill?.internalId ?? null,
          billingAddresseeName: null,
          billingAddrLine1: null,
          billingAddrLine2: null,
          billingAddrLine3: null,
          billingAddressOverrideJson: null,
        });
      }
    } catch (err) {
      // Cancelled by a newer FO select — bail without clobbering state/toasting.
      if (axios.isCancel(err) || signal?.aborted) return;
      // Or superseded by a newer draft load — stay silent (no spurious toast).
      if (isStaleLoad(loadSeq)) return;
      set({ isLoadingAddresses: false });
      toast.error(err instanceof Error ? err.message : 'Failed to load customer addresses');
    }
  },

  // 86d405z28 — the ONLY place an address is written to a customer's NetSuite record.
  // Call it from a user gesture (the dialog's Save, or the one-click PICK-UP create) and
  // NOWHERE else: never from a submit path, an effect, or a retry wrapper. The write cannot
  // be undone (NetSuite has no per-address DELETE), so a stray second call leaves a permanent
  // duplicate on real customer data — exactly the 86d3mnuxe FIX 5 defect.
  //
  // Errors are deliberately RE-THROWN, not toasted here: the caller reopens the dialog with the
  // typed values intact and shows NetSuite's raw message.
  appendCustomerAddress: async (customerId, request) => {
    const newId = await customerAddressService.append(customerId, request);
    // Refetch rather than splicing an optimistic row: only NetSuite knows the real internalId
    // and the canonical addrText, and the selection below points at that id.
    await get().loadCustomerAddresses(customerId);
    return newId;
  },

  setEligibilityBanner: (msg) => set({ eligibilityBanner: msg }),

  loadHierarchy: async (customerId, loadSeq) => {
    set({ isLoadingHierarchy: true });
    try {
      const hierarchy = await customerAddressService.hierarchy(customerId);
      // Superseded by a newer draft load — drop the stale hierarchy. The newer load's
      // own loadHierarchy resets isLoadingHierarchy, so bailing here can't strand it.
      if (isStaleLoad(loadSeq)) return;
      set({ customerHierarchy: hierarchy.companies, isLoadingHierarchy: false });
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (isStaleLoad(loadSeq)) return;
      set({ isLoadingHierarchy: false });
      // Non-fatal: the company selector falls back to just the FO customer.
      toast.error(err instanceof Error ? err.message : 'Failed to load company hierarchy');
    }
  },

  loadCompanyProfile: async (companyId, applyDefaults = false, loadSeq) => {
    set({ isLoadingProfile: true });
    try {
      const profile = await customerAddressService.profile(companyId);
      // Superseded by a newer draft load — drop the stale profile (and skip its
      // applyDefaults cascade, which would otherwise stamp the wrong draft's Terms/alerts).
      if (isStaleLoad(loadSeq)) return;
      set({ companyProfile: profile, isLoadingProfile: false });

      // Cascade defaults (FO-SOI-3): seed Terms + the 6 Document Alerts from the
      // selected company. Only on explicit pick / company change — never on draft
      // reopen (applyDefaults=false), so saved overrides survive.
      if (applyDefaults) {
        get().updateDraftFields({
          termsId: profile.termsId,
          termsName: profile.termsName,
          alertInvoiceECopy: profile.alerts.invoiceECopy,
          alertManualComputation: profile.alerts.manualComputation,
          alertMc2Copies: profile.alerts.mc2Copies,
          alertDrPicture: profile.alerts.drPicture,
          alertDrAttachment: profile.alerts.drAttachment,
          alertBrnTraceability: profile.alerts.brnTraceability,
          // Freight billing (86d3addwy): re-derive the SO separate-billing value from the
          // new company and reset the per-order freight-line choice so the coordinator
          // re-confirms (null = unconfirmed → prompt + billing-type default).
          separateBilling: separateBillingFromCustomer(profile.separateFreightYn),
          includeFreightLine: null,
        });
      }
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (isStaleLoad(loadSeq)) return;
      set({ isLoadingProfile: false, companyProfile: null });
      // Non-fatal: Terms/Tax/alerts simply show as unavailable.
    }
  },

  selectDoCustCompany: async (companyId, companyName) => {
    // Bump the shared load generation so this pick supersedes any in-flight cascade — an
    // earlier pick OR the draft-load cascade. Both loaders below receive `seq` and bail in
    // their isStaleLoad guard if a newer pick/load bumps it, so a superseded pick can't write
    // the wrong company's Terms/alerts/addresses into the store (86d3c5wwk #3).
    const seq = ++draftLoadSeq;
    // Stamp the chosen company immediately (instant feedback).
    get().updateDraftFields({ doCustCompanyId: companyId, doCustCompanyName: companyName });
    // AWAIT both cascades so the store holds the dependent defaults before the caller re-seeds
    // the form: the read-only profile (Terms/alerts/separate-billing) AND the address book
    // (applyDefaults resets shipping+billing). The profile used to be fire-and-forget, so its
    // defaults landed after the form reset and never reached the form (86d3c5wwk #1). Run them
    // in parallel — the re-seed only needs both settled, not ordered.
    await Promise.all([
      get().loadCompanyProfile(companyId, true, seq),
      get().loadCustomerAddresses(companyId, undefined, true, seq),
    ]);
    // Still the latest pick? The caller re-seeds the form only when true.
    return seq === draftLoadSeq;
  },
}));

// ─── Helpers ────────────────────────────────────────────────────────────────

function createBlankDraft(): SalesOrderDto {
  return {
    id: 0,
    salesOrderNumber: '',
    status: SalesOrderStatus.Draft,
    customerName: '',
    customerId: null,
    customerEntityId: null,
    headMainOffice: '',
    headMainOfficeCustomerId: null,
    projectName: '',
    termsId: null,
    termsName: null,
    salesRepId: null,
    salesRepName: null,
    salesCoordId: null,
    salesCoordName: null,
    linkedFirmOfferInternalId: null,
    linkedFirmOfferTranId: null,
    customerPoNumber: null,
    poExpiryDate: null,
    deliveryDueDate: null,
    contractExpiryDate: null,
    date: new Date().toISOString().slice(0, 10),
    shippingAddressId: null,
    shippingAddresseeName: null,
    shippingAddrLine1: null,
    shippingAddrLine2: null,
    shippingAddrLine3: null,
    shippingCityTownId: null,
    shippingStateProvinceId: null,
    shippingZip: null,
    shippingCountryId: 'PH',
    shippingAddressOverrideJson: null,
    doCustCompanyId: null,
    doCustCompanyName: null,
    billingAddressId: null,
    billingAddresseeName: null,
    billingAddrLine1: null,
    billingAddrLine2: null,
    billingAddrLine3: null,
    billingCityTownId: null,
    billingStateProvinceId: null,
    billingZip: null,
    billingCountryId: 'PH',
    billingAddressOverrideJson: null,
    scheduleId: null,
    scheduleName: null,
    authorizedReceivers: null,
    otherInstructions: null,
    soRemarks: null,
    subPoOrderlistLink: null,
    invoiceToLetter: null,
    officeContact1: null,
    contactNumber1: null,
    officeContact2: null,
    contactNumber2: null,
    officeContact3: null,
    contactNumber3: null,
    isTrailer: false,
    isMf: false,
    isPpeRequired: false,
    isPermitRequired: false,
    isPriority: false,
    isSpotOrder: false,
    isReprint: false,
    waitingForOfficialPo: false,
    alertInvoiceECopy: false,
    alertManualComputation: false,
    alertMc2Copies: false,
    alertDrPicture: false,
    alertDrAttachment: false,
    alertBrnTraceability: false,
    actualFreightPerMt: null,
    isDiscountedFreight: false,
    discountDetails: null,
    reasonForClosing: null,
    subagentId: null,
    waivedFreight: '2',
    separateBilling: '2',
    includeFreightLine: null,
    grade230Markup: null,
    grade275Markup: null,
    grade415Markup: null,
    grade520Markup: null,
    grade550Markup: null,
    overallPricePerKg: null,
    pricePerKg230G33: null,
    pricePerKg280G40Stv: null,
    pricePerKg420G60Stv: null,
    pricePerKg520G75Stv: null,
    pricePerKg550G80Stv: null,
    totalAuthorizedKg: null,
    rebarTaxRate: null,
    freightChargePerTon: null,
    perGradeBalanceKgJson: null,
    extractionSource: 'manual',
    extractionText: null,
    extractionFileName: null,
    extractionStatus: 0,
    netSuiteTranId: null,
    netSuiteInternalId: null,
    netSuiteUrl: null,
    postedAt: null,
    postedById: null,
    postedByName: null,
    preparedById: null,
    preparedByName: null,
    submittedSnapshotJson: null,
    perGradeRequestedKgJson: null,
    createdAt: new Date().toISOString(),
    createdById: 0,
    createdByName: null,
    updatedAt: null,
    isArchived: false,
    archivedAt: null,
    lineItems: [],
  };
}

function buildSaveRequest(draft: SalesOrderDto): SaveSalesOrderDraftRequest {
  return {
    id: draft.id > 0 ? draft.id : null,
    linkedFirmOfferInternalId: draft.linkedFirmOfferInternalId,
    linkedFirmOfferTranId: draft.linkedFirmOfferTranId,
    customerName: draft.customerName,
    customerId: draft.customerId,
    customerEntityId: draft.customerEntityId,
    headMainOffice: draft.headMainOffice,
    headMainOfficeCustomerId: draft.headMainOfficeCustomerId,
    projectName: draft.projectName,
    termsId: draft.termsId,
    termsName: draft.termsName,
    salesRepId: draft.salesRepId,
    salesRepName: draft.salesRepName,
    salesCoordId: draft.salesCoordId,
    salesCoordName: draft.salesCoordName,
    customerPoNumber: draft.customerPoNumber,
    poExpiryDate: draft.poExpiryDate,
    deliveryDueDate: draft.deliveryDueDate,
    contractExpiryDate: draft.contractExpiryDate,
    date: draft.date,
    shippingAddressId: draft.shippingAddressId,
    shippingAddresseeName: draft.shippingAddresseeName,
    shippingAddrLine1: draft.shippingAddrLine1,
    shippingAddrLine2: draft.shippingAddrLine2,
    shippingAddrLine3: draft.shippingAddrLine3,
    shippingCityTownId: draft.shippingCityTownId,
    shippingStateProvinceId: draft.shippingStateProvinceId,
    shippingZip: draft.shippingZip,
    shippingCountryId: draft.shippingCountryId,
    shippingAddressOverrideJson: draft.shippingAddressOverrideJson,
    doCustCompanyId: draft.doCustCompanyId,
    doCustCompanyName: draft.doCustCompanyName,
    billingAddressId: draft.billingAddressId,
    billingAddresseeName: draft.billingAddresseeName,
    billingAddrLine1: draft.billingAddrLine1,
    billingAddrLine2: draft.billingAddrLine2,
    billingAddrLine3: draft.billingAddrLine3,
    billingCityTownId: draft.billingCityTownId,
    billingStateProvinceId: draft.billingStateProvinceId,
    billingZip: draft.billingZip,
    billingCountryId: draft.billingCountryId,
    billingAddressOverrideJson: draft.billingAddressOverrideJson,
    scheduleId: draft.scheduleId,
    scheduleName: draft.scheduleName,
    authorizedReceivers: draft.authorizedReceivers,
    otherInstructions: draft.otherInstructions,
    soRemarks: draft.soRemarks,
    subPoOrderlistLink: draft.subPoOrderlistLink,
    invoiceToLetter: draft.invoiceToLetter,
    officeContact1: draft.officeContact1,
    contactNumber1: draft.contactNumber1,
    officeContact2: draft.officeContact2,
    contactNumber2: draft.contactNumber2,
    officeContact3: draft.officeContact3,
    contactNumber3: draft.contactNumber3,
    isTrailer: draft.isTrailer,
    isMf: draft.isMf,
    isPpeRequired: draft.isPpeRequired,
    isPermitRequired: draft.isPermitRequired,
    isPriority: draft.isPriority,
    isSpotOrder: draft.isSpotOrder,
    isReprint: draft.isReprint,
    waitingForOfficialPo: draft.waitingForOfficialPo,
    alertInvoiceECopy: draft.alertInvoiceECopy,
    alertManualComputation: draft.alertManualComputation,
    alertMc2Copies: draft.alertMc2Copies,
    alertDrPicture: draft.alertDrPicture,
    alertDrAttachment: draft.alertDrAttachment,
    alertBrnTraceability: draft.alertBrnTraceability,
    actualFreightPerMt: draft.actualFreightPerMt,
    isDiscountedFreight: draft.isDiscountedFreight,
    discountDetails: draft.discountDetails,
    reasonForClosing: draft.reasonForClosing,
    subagentId: draft.subagentId,
    waivedFreight: draft.waivedFreight,
    separateBilling: draft.separateBilling,
    includeFreightLine: draft.includeFreightLine,
    grade230Markup: draft.grade230Markup,
    grade275Markup: draft.grade275Markup,
    grade415Markup: draft.grade415Markup,
    grade520Markup: draft.grade520Markup,
    grade550Markup: draft.grade550Markup,
    overallPricePerKg: draft.overallPricePerKg,
    pricePerKg230G33: draft.pricePerKg230G33,
    pricePerKg280G40Stv: draft.pricePerKg280G40Stv,
    pricePerKg420G60Stv: draft.pricePerKg420G60Stv,
    pricePerKg520G75Stv: draft.pricePerKg520G75Stv,
    pricePerKg550G80Stv: draft.pricePerKg550G80Stv,
    totalAuthorizedKg: draft.totalAuthorizedKg,
    rebarTaxRate: draft.rebarTaxRate,
    freightChargePerTon: draft.freightChargePerTon,
    perGradeBalanceKgJson: draft.perGradeBalanceKgJson,
    extractionSource: draft.extractionSource,
    extractionText: draft.extractionText,
    extractionFileName: draft.extractionFileName,
    lineItems: draft.lineItems.map((li) => ({
      clientId: li.clientId,
      sortOrder: li.sortOrder,
      diameterMm: li.diameterMm,
      lengthM: li.lengthM,
      gradeCode: li.gradeCode,
      isWeldable: li.isWeldable,
      quantity: li.quantity,
      // 14ykpqtv3qw — stored as pieces, weighed pieces × kg per piece (the server re-stamps both on save).
      unit: PIECE_UNIT,
      weightKg: lineWeightKg(li),
      weightPerPcKg: li.weightPerPcKg,
      matchStatus: li.matchStatus,
      rawDescription: li.rawDescription,
      notes: li.notes,
      matchedSalesDescription: li.matchedSalesDescription,
      matchedItemId: li.matchedItemId,
      priceLevel: li.priceLevel,
      basePricePerPc: li.basePricePerPc,
      averagePricePerPc: li.averagePricePerPc,
      originalSpecJson: li.originalSpecJson,
      isEdited: li.isEdited,
      promotedFromGrade: li.promotedFromGrade,
    })),
  };
}
